"use client";

// The front door (#396). Until 2026-09-02 this file was the Phase-0
// toolchain shell ("Modules land here part by part") — every journey behind
// it had been built to parity and the door itself never replaced, so the
// first URL a VIP user types after cutover would have shown a placeholder
// sentence. Matt found it on the demo box.
//
// Under the SERVED vip brand this is reference/scopely-frontend/src/app/
// page.tsx + components/HomeHero.tsx, ported: the animated grid backdrop,
// the theme toggle floated top-right, the PSCX VIP lockup, the three lines
// of copy (character-identical), Login -> /login, Request Access ->
// /register. Its a11y inventory is pinned to parity-capture/fixtures/
// landing.txt by landing.parity.test.tsx.
//
// Declared divergences (same posture as the login page's header):
//   - AnimatedGradientText: the reference retired its gradient and renders
//     solid `text-accent` (its own component comment says so). Ported as the
//     span it now is; no separate component for a one-line wrapper.
//   - AnimatedGridPattern: CSS-driven port, not `motion/react` — see
//     shared/ui/AnimatedGridPattern.tsx.
//   - Under any OTHER brand (zw, or the pre-bootstrap default) the hero is
//     VIP marketing copy that ZW has never shown its users, so the page
//     renders the plain brand heading with a single Sign in link instead.
//     ZW users reach this app's root only by typing it: their product is
//     the legacy front end. useCurrentBrand resolves on mount, so the page
//     never REDIRECTS on brand — a redirect would fire on the default brand
//     before the served one had a chance to arrive. Two consequences, both
//     the same declared class as /login and /forgot-password (D1): the
//     static prerender of `/` IS the plain variant, so a VIP visitor sees it
//     for the bootstrap window; and if the bootstrap itself fails
//     (ExperienceProvider keeps the default brand on 503/transport error)
//     the plain variant is what that visitor keeps. The named fix for both
//     is a server-rendered brand pass, out of this page's scope.
import Link from "next/link";
import { ArrowRight } from "lucide-react";

import { AnimatedGridPattern } from "@/shared/ui/AnimatedGridPattern";
import { brandProfile, ThemeToggle, useCurrentBrand } from "@/shared/ui";

/** The reference's brand lockup, same idiom as the login page's copy. */
function PscxVipLockup() {
  return (
    <div className="mb-8 flex items-center">
      {/* eslint-disable-next-line @next/next/no-img-element -- static brand asset, same idiom as the reference */}
      <img src="/pscx-vip-logo.svg" alt="PSCX VIP" className="h-32 w-auto sm:h-40" />
    </div>
  );
}

function VipLanding() {
  return (
    <div className="relative min-h-screen flex flex-col bg-page overflow-hidden">
      <AnimatedGridPattern
        numSquares={28}
        maxOpacity={0.08}
        duration={4}
        repeatDelay={1}
        className="[mask-image:radial-gradient(700px_circle_at_center,white,transparent)] inset-x-0 inset-y-[-10%] h-[120%] skew-y-6 text-accent/60"
      />
      <div className="absolute top-4 right-4 z-20 sm:top-6 sm:right-6">
        <ThemeToggle />
      </div>
      <main className="relative z-10 flex-1 flex flex-col items-center px-6 sm:px-10">
        <section className="relative flex min-h-screen w-full flex-col items-center justify-center px-6 py-20 text-center sm:px-10">
          <div className="hero-enter flex max-w-5xl flex-col items-center">
            <PscxVipLockup />
            <h1 className="max-w-4xl text-5xl font-black tracking-tight text-ink sm:text-7xl">
              Voice Intelligence Platform
            </h1>
            <p className="mt-6 text-2xl font-semibold sm:text-3xl">
              <span className="inline text-accent">Revenue-as-a-Service</span>
            </p>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-ink-light">
              Create SOWs, manage migrations, and capture services revenue in one dashboard
            </p>
            <div className="mt-9 flex flex-col items-center gap-3 sm:flex-row">
              <Link href="/login" className="btn-hero flex items-center gap-2">
                Login
                <ArrowRight size={16} />
              </Link>
              <Link
                href="/register"
                className="px-5 py-2.5 rounded-lg border border-edge bg-surface/60 text-sm font-medium text-ink-light hover:text-ink hover:border-ink transition-colors backdrop-blur-sm"
              >
                Request Access
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function PlainLanding({ brandName }: { brandName: string }) {
  return (
    <div className="min-h-screen bg-page px-6 py-10">
      <main className="mx-auto flex min-h-[calc(100vh-5rem)] w-full max-w-md flex-col items-center justify-center gap-6 text-center">
        <h1 className="text-3xl font-black tracking-tight text-ink">{brandName}</h1>
        <Link href="/login" className="btn-hero flex items-center gap-2">
          Sign in
          <ArrowRight size={16} />
        </Link>
      </main>
    </div>
  );
}

export default function LandingPage() {
  const brand = useCurrentBrand();
  // The heading comes from the brand profile (review: no second copy of a
  // brand name), so a future non-vip surface announces itself, not "ZoomWarriors".
  return brand === "vip" ? <VipLanding /> : <PlainLanding brandName={brandProfile(brand).name} />;
}
